import { useEffect, useRef, useState } from 'react';
import { imageTrailsService } from '../services/imageTrails';
import { activeCo } from '../App';

const CursorImageTrail = ({ src = '/logo.png', size = 80, threshold = 60 }) => {
  const lastPos = useRef({ x: 0, y: 0 });
  const active = useRef(false);
  const [resolvedSrc, setResolvedSrc] = useState(src);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const co = params.get('co') || activeCo;
    if (!co) {
      setResolvedSrc(src);
      return;
    }

    imageTrailsService.getAllImageTrails().then(trails => {
      const match = trails.find(t => t.name.toLowerCase() === co.toLowerCase());
      const imageUrl = match ? match.imageUrl : src;
      setResolvedSrc(imageUrl);

      // Preload the image immediately
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = imageUrl;
      document.head.appendChild(link);
    }).catch(() => {
      setResolvedSrc(src);
    });
  }, [src]);

  useEffect(() => {
    // Desktop only
    if (window.matchMedia('(hover: none)').matches) return;

    const handleMouseMove = (e) => {
      const dx = e.clientX - lastPos.current.x;
      const dy = e.clientY - lastPos.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < threshold) return;

      lastPos.current = { x: e.clientX, y: e.clientY };

      const img = document.createElement('img');
      img.src = resolvedSrc;

      const rotation = (Math.random() - 0.5) * 16;

      img.style.cssText = `
        position: fixed;
        left: ${e.clientX}px;
        top: ${e.clientY}px;
        width: ${size}px;
        height: ${size}px;
        object-fit: contain;
        transform: translate(-50%, -50%) scale(0.5) rotate(${rotation}deg);
        pointer-events: none;
        z-index: 99999;
        opacity: 0.025;
        transition: opacity 0.8s ease, transform 0.8s ease;
        border-radius: 8px;
      `;

      document.body.appendChild(img);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          img.style.transform = `translate(-50%, -50%) scale(1) rotate(${rotation}deg)`;
        });
      });

      setTimeout(() => {
        img.style.opacity = '0';
        img.style.transform = `translate(-50%, -50%) scale(1.2) rotate(${rotation}deg)`;
      }, 300);

      setTimeout(() => img.remove(), 1100);
    };

    const handleMouseEnter = () => { active.current = true; };
    const handleMouseLeave = () => { active.current = false; };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseenter', handleMouseEnter);
    window.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseenter', handleMouseEnter);
      window.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [resolvedSrc, size, threshold]);

  return null;
};

export default CursorImageTrail;
